import _ from "lodash";
import moment from "moment";
import PropTypes from "prop-types";
import React, { useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import submit from "submit-form";
import { MUSCLE_GROUP_ARRAY } from "../../../../constants";
import { getEffectiveDefaultInsightsLens } from "../../../../constants/default-insights-lens";
import DateInput from "../../../../parts/inputs/date-input";
import PRBadge from "../../../../parts/PRBadge";
import SvgIcon from "../../../../parts/SvgIcon";
import ToolTip from "../../../../parts/tooltip";
import { formattedTarget } from "../../../../utils/actuals";
// import { useHorizontalScroll } from "../../../../hooks/useHorizontalScroll";
import {
  findPreviousValue,
  getBlockData,
  getMetricsData,
  getPeriods,
} from "../../../../utils/block-review-helpers";
import { groupExercisesByComplex } from "../../../../utils/complex";
import getAvatarUrl from "../../../../utils/get-avatar-url";
import { getFullName } from "../../../../utils/personal-records";
import { buildVideoModalUrl } from "../../../../utils/video-modal-url";
import NavigationComponent from "../../calendar/components/NavigationComponent";
import FilterChips from "../../calendar/FilterChips";
import ProgramInsightsShell from "../../programs/program-insights/components/ProgramInsightsShell";
import {
  buildDateRangeInsightsInput,
  dateRangeInsightsStorageId,
} from "../../programs/program-insights/utils/inputs/buildDateRangeInsightsInput";
import { INSIGHTS_DATA_SOURCE } from "../../programs/program-insights/utils/insightsSetRowMath";
import { getExerciseName } from "../../../../parts/modals/learn-more/blockHelpers";
import TrendIndicator from "./TrendIndicator";

export const BLOCK_REVIEW_INSIGHTS_SHELL_CLASS = "BlockReview--insights-shell";

const muscleGroupToI18nKey = (val) =>
  val
    .replace(/\s+/g, "")
    .replace(/[^a-zA-Z0-9]/g, "")
    .replace(/^[A-Z]/, (c) => c.toLowerCase());

const BlockReview = ({ locals }) => {
  const {
    query,
    pathname,
    weeksExercises,
    activeUser,
    activeUserToRequest,
    withWeight,
    fromMetric,
    organizationOwner,
    clientConfiguration,
  } = locals;

  const athleteId = locals.athlete?._id || activeUser._id;

  const { t } = useTranslation(["athlete-portal", "common"]);
  const repMaxPR = locals.dayPersonalRecords?.repMaxPR;
  const { units } = activeUser;
  const precision = units === "metric" ? 2 : 1;
  const unitsLabel = units === "metric" ? "kg" : "lb";

  const from = query.from
    ? query.from
    : moment().subtract(4, "weeks").format("YYYY-MM-DD");
  const to = query.to ? query.to : moment().format("YYYY-MM-DD");

  const startingCalendarDay = activeUser.startingCalendarDay || 0;

  const [filteredWeeks, setFilteredWeeks] = useState([]);
  const [showTrends, setShowTrends] = useState(true);
  const [periods, setPeriods] = useState([]);
  const [exerciseBlockData, setExerciseBlockData] = useState([]);
  const [exerciseBlockDataToShow, setExerciseBlockDataToShow] = useState([]);
  const [metricsBlockData, setMetricsBlockData] = useState({});

  const metricsDataKeys = Object.keys(metricsBlockData).sort();

  const intensityMeasure =
    _.get(activeUserToRequest, "intensityMeasure", "rpe") === "rpe"
      ? "RPE"
      : "RiR";

  const ref = useRef(null);
  const tableScrollRef = useRef(null);
  const [left, setLeft] = useState(0);

  // Memo to avoid re-rendering the insights when the filtered weeks change
  const exercisesForInsights = useMemo(() => {
    if (!filteredWeeks.length) {
      return [];
    }

    if (!query.exerciseFilters) {
      return filteredWeeks;
    }

    let filters = [];
    try {
      filters = JSON.parse(query.exerciseFilters);
    } catch {
      return filteredWeeks;
    }

    if (!filters.length) {
      return filteredWeeks;
    }

    return filteredWeeks.filter((exercise) =>
      filters.includes(getExerciseName(exercise))
    );
  }, [filteredWeeks, query.exerciseFilters]);

  const programInsightsInput = useMemo(
    () =>
      buildDateRangeInsightsInput({
        exercises: exercisesForInsights,
        from,
        to,
        startingCalendarDay,
      }),
    [exercisesForInsights, from, to, startingCalendarDay]
  );

  const insightsActive =
    Boolean(programInsightsInput) && exerciseBlockDataToShow.length > 0;
  const insightsStorageId = dateRangeInsightsStorageId(
    "block-review",
    athleteId,
    from,
    to
  );

  // useEffect to reject the user from entering the report while responsive is not ready
  useEffect(() => {
    if (window.innerWidth <= 960) {
      history.back();
    }
  }, []);

  useEffect(() => {
    if (ref.current) {
      const firstChildWidth = ref.current.offsetWidth;

      if (firstChildWidth) {
        setLeft(firstChildWidth);
      }
    }
  }, [exerciseBlockData]);

  useEffect(() => {
    // Filter out exercises without outcomes
    const filteredArray = weeksExercises.filter((item) =>
      item.sets?.some((set) => set.outcomes)
    );

    // Group complex exercises before sorting
    const groupedArray = groupExercisesByComplex(filteredArray);

    const sortedArray = groupedArray.sort((a, b) => a.utcDate - b.utcDate);

    setFilteredWeeks(sortedArray);
  }, [weeksExercises]);

  useEffect(() => {
    if (filteredWeeks.length > 0) {
      const blockData = getBlockData(
        filteredWeeks,
        from,
        to,
        startingCalendarDay,
        intensityMeasure,
        activeUser,
        withWeight,
        fromMetric
      );

      const metricsData = getMetricsData(
        filteredWeeks,
        startingCalendarDay,
        from,
        to
      );

      const periods = getPeriods(from, to, startingCalendarDay);

      setPeriods(periods);
      setExerciseBlockData(blockData);
      setExerciseBlockDataToShow(blockData);
      setMetricsBlockData(metricsData);

      // Filters the exercises
      // Put in here because outside the useEffect would cause sync issues
      if (
        query.exerciseFilters &&
        JSON.parse(query.exerciseFilters).length > 0
      ) {
        const newData = blockData.filter((exercise) =>
          JSON.parse(query.exerciseFilters).includes(exercise.name)
        );

        const metricsData = getMetricsData(
          filteredWeeks,
          startingCalendarDay,
          from,
          to,
          JSON.parse(query.exerciseFilters)
        );

        setMetricsBlockData(metricsData);
        setExerciseBlockDataToShow(newData);
      }
    } else {
      setPeriods([]);
      setExerciseBlockData([]);
      setExerciseBlockDataToShow([]);
      setMetricsBlockData({});
    }
  }, [filteredWeeks, query.exerciseFilters]);

  const handleDateChange = (date, field) => {
    submit("/actions/refresh", {
      method: "GET",
      body: { [field]: date },
    });
  };

  const handleActiveTab = (tab) => {
    setTabState(tab);
    setDateRange(tab);
  };

  //start date
  const [startDateToShow, setStartDateToShow] = useState(
    query.from
      ? moment(query.from)
      : moment().subtract(4, "weeks").format("MMM DD, YYYY")
  );

  //end state
  const [endDateToShow, setEndDateToShow] = useState(
    query.to ? moment(query.to) : moment()
  );

  //control the date range tab state (1 month, 3 months... lifetime...)
  const [tabState, setTabState] = useState(
    query.activeRange ? query.activeRange : 4
  );

  const setDateRange = (tab) => {
    const today = moment();
    const lifetimeDate = "2015-12-31T07:57:45.121Z"; //date when MSB starts
    const weeksToSubtract = Number(tab);
    const periodDate = weeksToSubtract
      ? moment().subtract(weeksToSubtract, "weeks")
      : moment(lifetimeDate);
    const startFormat = periodDate.format("YYYY-MM-DD");
    const endFormat = today.format("YYYY-MM-DD");
    setStartDateToShow(periodDate.format("MMM DD, YYYY"));
    setEndDateToShow(today.format("MMM DD, YYYY"));

    submit("/actions/refresh/?", {
      method: "GET",
      body: {
        from: startFormat,
        to: endFormat,
        activeRange: tab,
      },
    });
  };

  const openFilterModal = () => {
    const filters = exerciseBlockData.map((exercise) => exercise.name);

    submit("/actions/refresh/?", {
      method: "GET",
      body: {
        "modal[type]": "filter-block-review-modal",
        filtersModal: JSON.stringify(filters),
      },
    });
  };

  const removeFilter = (type) => {
    const newFiltersSet = JSON.parse(query.exerciseFilters).filter(
      (exercise) => exercise !== type
    );
    submit(`/actions/refresh/?`, {
      method: "GET",
      body: {
        exerciseFilters:
          newFiltersSet.length > 0 ? JSON.stringify(newFiltersSet) : "",
      },
    });
  };

  const clearAllFilters = () => {
    submit(`/actions/refresh/?`, {
      method: "GET",
      body: {
        exerciseFilters: "",
        modal: "",
        filtersModal: "",
      },
    });
  };

  return (
    <main
      role="reports-wrapper"
      className={`block-review${
        insightsActive ? ` ${BLOCK_REVIEW_INSIGHTS_SHELL_CLASS}` : ""
      }`}
    >
      <NavigationComponent
        query={query}
        textKey="nav.calendarDayView"
        pathName={pathname}
      />

      <article
        role="report-content"
        id="block-review"
        className={insightsActive ? BLOCK_REVIEW_INSIGHTS_SHELL_CLASS : undefined}
      >
        <header>
          <h2>{t("blockReview.title")}</h2>

          <div>
            {query.exerciseFilters ? (
              <FilterChips
                filters={
                  query.exerciseFilters
                    ? JSON.parse(query.exerciseFilters).map((filter) => {
                        return { text: filter, id: filter };
                      })
                    : []
                }
                clearAllFilters={clearAllFilters}
                clearFilter={removeFilter}
              />
            ) : null}
            <button
              type="button"
              role="switch"
              aria-checked={showTrends}
              className="trend-toggle"
              onClick={() => setShowTrends((prev) => !prev)}
            >
              <span className="trend-toggle-track">
                <span className="trend-toggle-knob" />
              </span>
              <span className="trend-toggle-label">
                {t("blockReview.trendIndicators")}
              </span>
            </button>
            <button
              data-button-type="secondary"
              sz="medium"
              onClick={openFilterModal}
              type="button"
            >
              <SvgIcon src="/img/new-icons/filter-white.svg" />
              <b>{t("common:filter")}</b>
            </button>
          </div>
        </header>

        <div className="Date-Range">
          <div role="calendar-date-selectors">
            <DateInput
              name="from"
              placeholder={t("blockReview.startDate")}
              value={startDateToShow}
              max={query.to}
              onChange={(date) => handleDateChange(date, "from")}
            />
            <span>{t("blockReview.to")}</span>
            <DateInput
              name="to"
              placeholder={t("blockReview.endDate")}
              value={endDateToShow}
              min={query.from}
              onChange={(date) => handleDateChange(date, "to")}
            />
          </div>

          <div
            className="right-side"
            id="tabsSection"
            style={{ display: "flex", gap: "4px" }}
          >
            {[4, 8, 12, 16, 20, 24].map((weeks) => (
              <button
                data-button-type={tabState === weeks ? "primary" : "secondary"}
                id={`${weeks}-weeks-tab`}
                key={`${weeks}-weeks-tab`}
                onClick={() => handleActiveTab(weeks)}
                type="button"
              >
                {t("blockReview.weeksButton", { count: weeks })}
              </button>
            ))}
          </div>
        </div>

        <div className="block-review-legend">
          <div className="block-review-legend-item">
            <div className={STATUS_CLASS_NAME.completed}></div>
            <span>{t("blockReview.completed")}</span>
          </div>
          <div className="block-review-legend-item">
            <div className={STATUS_CLASS_NAME.missed}></div>
            <span>{t("blockReview.missed")}</span>
          </div>
          <div className="block-review-legend-item">
            <div className={STATUS_CLASS_NAME.incomplete}></div>
            <span>{t("blockReview.incomplete")}</span>
          </div>
        </div>

        {showTrends && (
          <div className="block-review-trend-legend">
            <span className="trend-legend-title">
              {t("blockReview.trendLegendTitle")}
            </span>
            <span className="block-review-legend-item">
              <TrendIndicator curr={1} prev={0} metric="reps" />
              {t("blockReview.increase")}
            </span>
            <span className="block-review-legend-item">
              <TrendIndicator curr={0} prev={1} metric="reps" />
              {t("blockReview.decrease")}
            </span>
            <span className="block-review-legend-item">
              <TrendIndicator curr={0} prev={0} metric="reps" />
              {t("blockReview.noChange")}
            </span>
            <span className="trend-legend-sep" />
            <span className="trend-legend-keys">
              {t("blockReview.trendKeysHint", { intensity: intensityMeasure })}
            </span>
            <span className="trend-legend-sep" />
            <span className="block-review-legend-item">
              <TrendIndicator curr={1} prev={0} metric="rpe" />
              {t("blockReview.rpeHarder", { intensity: intensityMeasure })}
            </span>
            <span className="block-review-legend-item">
              <TrendIndicator curr={0} prev={1} metric="rpe" />
              {t("blockReview.rpeEasier", { intensity: intensityMeasure })}
            </span>
          </div>
        )}

        <div
          className={`ProgramEditor-insights-body${
            insightsActive ? " ProgramEditor-insights-body--stacked" : ""
          }`}
        >
          <section ref={tableScrollRef} role="table-wrapper">
          {exerciseBlockDataToShow.length ? (
            <table role="table-wrapper">
              <thead role="table-header">
                <tr>
                  <th>
                    <span>{t("blockReview.exerciseName")}</span>
                  </th>
                  <th style={{ left: left - 28 }}>
                    <span>{t("blockReview.sets")}</span>
                  </th>
                  {periods.map((period) => (
                    <th key={`${period}-period-item`} colSpan={2}>
                      <span>{period}</span>
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody role="table-body">
                {exerciseBlockDataToShow.map((exerciseData, index) => {
                  const shownPRReps = new Set();
                  return (
                    <tr
                      key={`${exerciseData.name}-row`}
                      className="block-review-target-row"
                    >
                      {/* Exercise Name Column */}
                      <td
                        key={`${exerciseData.name}-${index}-name`}
                        className="min-w-40"
                        ref={ref}
                      >
                        <p>{_.upperFirst(exerciseData.name)}</p>
                      </td>
                      {/* Sessions and Sets Column if only 1 Session */}
                      {exerciseData.sessions === 1 && (
                        <td
                          className="session-cell"
                          style={{ left: left - 28 }}
                        >
                          <div className="session-titles session-titles--sets">
                            {_.times(exerciseData.numberOfSets[0]).map(
                              (index) => (
                                <p
                                  key={`${exerciseData.name}-set-${exerciseData.numberOfSets[0]}-${index}`}
                                >
                                  {t("blockReview.setLabel", {
                                    number: index + 1,
                                  })}
                                </p>
                              )
                            )}
                          </div>

                          <div className="extra-session">
                            <p className="s1">{t("blockReview.metrics")}</p>
                          </div>
                          <div className="session-titles">
                            <p>{t("blockReview.maxWeight")}</p>
                            <p>{t("blockReview.topE1rm")}</p>
                            <p>{t("blockReview.volume")}</p>
                          </div>
                        </td>
                      )}
                      {/* Sessions and Sets Column if more than 1 session */}
                      {exerciseData.sessions > 1 && (
                        <td
                          className="session-cell"
                          style={{ left: left - 28 }}
                        >
                          {_.times(exerciseData.sessions).map(
                            (sessionIndex) => (
                              <React.Fragment
                                key={`${sessionIndex}-set-extra-session`}
                              >
                                <div className="extra-session">
                                  <p className="s1">
                                    {t("blockReview.sessionLabel", {
                                      number: sessionIndex + 1,
                                    })}
                                  </p>
                                </div>
                                <div className="session-titles session-titles--sets">
                                  {_.times(
                                    exerciseData.numberOfSets[sessionIndex]
                                  ).map((setIndex) => (
                                    <p
                                      key={`${exerciseData.name}-set-${exerciseData.numberOfSets[sessionIndex]}-${setIndex}`}
                                    >
                                      {t("blockReview.setLabel", {
                                        number: setIndex + 1,
                                      })}
                                    </p>
                                  ))}
                                </div>
                              </React.Fragment>
                            )
                          )}
                          <div className="extra-session">
                            <p className="s1">{t("blockReview.metrics")}</p>
                          </div>
                          <div className="session-titles">
                            <p>{t("blockReview.maxWeight")}</p>
                            <p>{t("blockReview.topE1rm")}</p>
                            <p>{t("blockReview.volume")}</p>
                          </div>
                        </td>
                      )}
                      {/* Targets */}
                      {Object.entries(exerciseData.periods)
                        .sort((a, b) => {
                          return (
                            new Date(a[1].startOfWeek) -
                            new Date(b[1].startOfWeek)
                          );
                        })
                        .map(([periodKey], periodIndex, sortedPeriods) => {
                          // Week-over-week trend comparison. For each value we
                          // look back through earlier weeks in range and compare
                          // against the most recent prior week that actually
                          // logged it (a missing/absent prior week is skipped).
                          // The per-set R/E/L look-backs are computed inline
                          // below since they depend on session + set position.
                          const prevMaxWeight = findPreviousValue(
                            sortedPeriods,
                            periodIndex,
                            (p) => p.maxWeight || null
                          );
                          const prevTopE1rm = findPreviousValue(
                            sortedPeriods,
                            periodIndex,
                            (p) => p.topE1rm || null
                          );
                          const prevVolume = findPreviousValue(
                            sortedPeriods,
                            periodIndex,
                            (p) => p.volume || null
                          );

                          return (
                            <td
                              key={`${periodKey}-${exerciseData.name}`}
                              colSpan={2}
                              className="targets-td"
                            >
                            <div className="targets-div">
                              {!!exerciseData.periods[periodKey].hasData &&
                                _.times(exerciseData.sessions).map(
                                  (sessionIndex) => (
                                    <div
                                      className={
                                        exerciseData.sessions > 1
                                          ? "block-review-extra-session-container"
                                          : "block-review-session-container"
                                      }
                                      key={`${periodKey}-${exerciseData.name}-${sessionIndex}`}
                                    >
                                      {exerciseData.sessions > 1 && (
                                        <p className="blank-target">&nbsp;</p>
                                      )}
                                      {Object.keys(
                                        exerciseData.periods[periodKey]
                                          .outcomes[sessionIndex + 1] || {}
                                      ).map((outcomeIndex) => {
                                        const {
                                          status,
                                          comment,
                                          target,
                                          video,
                                          exerciseId,
                                          utcDate,
                                          outcomeLoad,
                                          outcomeReps,
                                          outcomeRpe,
                                          exerciseType,
                                          exercisePrimary,
                                          exerciseSecondary,
                                        } =
                                          exerciseData.periods[periodKey]
                                            .outcomes[sessionIndex + 1][
                                            outcomeIndex
                                          ];

                                        let modalRedirect = null;
                                        let lastCommentUser = null;
                                        let lastCommentUserAvatar = null;

                                        if (video) {
                                          const sourceExercise =
                                            filteredWeeks.find(
                                              (ex) =>
                                                String(ex._id) ===
                                                String(exerciseId)
                                            );
                                          const exercisePrimary =
                                            sourceExercise?.primary ??
                                            sourceExercise?.exercise?.primary ??
                                            "";
                                          const exerciseSecondary =
                                            sourceExercise?.secondary ??
                                            sourceExercise?.exercise
                                              ?.secondary ??
                                            "";
                                          const exerciseType =
                                            sourceExercise?.type ??
                                            sourceExercise?.exercise?.type ??
                                            "";
                                          const exerciseMovement =
                                            sourceExercise?.movement ??
                                            sourceExercise?.exercise
                                              ?.movement ??
                                            "";
                                          const seqOutcomeObj = {
                                            load: outcomeLoad,
                                            reps: outcomeReps,
                                            rpe: outcomeRpe,
                                          };
                                          const seqIsPowerlifting =
                                            exerciseType === "primary" ||
                                            exerciseType === "secondary";
                                          const seqFromMetric =
                                            fromMetric || ((v) => v);
                                          const seqPrecision =
                                            units === "metric" ? 2 : 1;
                                          const seqUserMetrics =
                                            units === "imperial" ? "lb" : "kg";
                                          const seqRawLoad = outcomeLoad;
                                          const seqLoad = seqRawLoad
                                            ? `${_.round(seqFromMetric(seqRawLoad, units), seqPrecision)} ${seqUserMetrics}`
                                            : 0;
                                          const seqIntensityMeasure =
                                            activeUser.intensityMeasure;
                                          const seqTarget =
                                            seqRawLoad ||
                                            outcomeReps ||
                                            outcomeRpe
                                              ? formattedTarget(
                                                  seqOutcomeObj,
                                                  seqIsPowerlifting,
                                                  undefined,
                                                  units,
                                                  seqLoad,
                                                  seqIntensityMeasure,
                                                  seqIntensityMeasure !== "rpe",
                                                  true
                                                ) || undefined
                                              : undefined;
                                          const seqSetNumber =
                                            Number(outcomeIndex) + 1;
                                          modalRedirect = buildVideoModalUrl({
                                            modalType: "exercise-set-video",
                                            video,
                                            exerciseToEdit: exerciseId,
                                            setIndex: sessionIndex,
                                            videoIndex: outcomeIndex,
                                            utcDate,
                                            primary: exercisePrimary,
                                            secondary: exerciseSecondary,
                                            type: exerciseType,
                                            movement: exerciseMovement,
                                            setNumber: seqSetNumber,
                                            setTarget: seqTarget,
                                            setComment: comment || undefined,
                                          });
                                          const lastComment =
                                            video.thread?.lastComment;
                                          lastCommentUser =
                                            lastComment?.sender ??
                                            [locals.athlete, activeUser].find(
                                              (user) =>
                                                user?._id ===
                                                lastComment?.senderId
                                            );
                                          lastCommentUserAvatar =
                                            getAvatarUrl(lastCommentUser);
                                        }

                                        return (
                                          outcomeIndex >= 0 && (
                                            <div
                                              className="block-review-target-container"
                                              key={`${periodKey}-${exerciseData.name}-${sessionIndex}-${outcomeIndex}`}
                                            >
                                              {target ? (
                                                <div className="block-review-target">
                                                  {status && (
                                                    <div
                                                      className={
                                                        STATUS_CLASS_NAME[
                                                          status
                                                        ]
                                                      }
                                                    ></div>
                                                  )}
                                                  <p>{target}</p>
                                                </div>
                                              ) : (
                                                <p>&nbsp;</p>
                                              )}

                                              <div className="block-review-icon-container">
                                                {comment && (
                                                  <div className="block-review-icon">
                                                    <ToolTip
                                                      id={`${exerciseData.name}-${periodKey}-${sessionIndex}-${outcomeIndex}-comment`}
                                                      body={comment}
                                                    >
                                                      <SvgIcon src="/img/new-icons/vuesax-linear-message-text.svg" />
                                                    </ToolTip>
                                                  </div>
                                                )}
                                                {video && (
                                                  <a
                                                    className="block-review-icon play-icon-wrapper"
                                                    href={modalRedirect}
                                                  >
                                                    <SvgIcon
                                                      classNames={`play-icon${
                                                        video.thread
                                                          ? " threaded"
                                                          : ""
                                                      }`}
                                                      src="/img/new-icons/play-gradient.svg"
                                                    />
                                                    {video.thread && (
                                                      <img
                                                        className="avatar"
                                                        src={
                                                          lastCommentUserAvatar
                                                        }
                                                        alt="last comment user avatar"
                                                        loading="lazy"
                                                      />
                                                    )}
                                                  </a>
                                                )}
                                                {(() => {
                                                  if (
                                                    status !== "completed" ||
                                                    !outcomeReps ||
                                                    !outcomeLoad ||
                                                    !repMaxPR ||
                                                    (outcomeRpe &&
                                                      outcomeRpe !== "AMRAP" &&
                                                      Number(outcomeRpe) < 7)
                                                  )
                                                    return null;
                                                  const prKey = getFullName({
                                                    type: exerciseType,
                                                    primary: exercisePrimary,
                                                    secondary:
                                                      exerciseSecondary,
                                                  });
                                                  const prData =
                                                    repMaxPR?.[prKey]?.[
                                                      String(outcomeReps)
                                                    ];
                                                  if (
                                                    prData &&
                                                    prData.load ===
                                                      outcomeLoad &&
                                                    prData.delta >= 0 &&
                                                    !shownPRReps.has(
                                                      outcomeReps
                                                    )
                                                  ) {
                                                    shownPRReps.add(
                                                      outcomeReps
                                                    );
                                                    return (
                                                      <PRBadge
                                                        reps={outcomeReps}
                                                      />
                                                    );
                                                  }
                                                  return null;
                                                })()}
                                                {showTrends &&
                                                  (() => {
                                                    // Look each value back
                                                    // independently to the most
                                                    // recent prior week that
                                                    // logged it at this session +
                                                    // set position.
                                                    const at = (p) =>
                                                      p.outcomes?.[
                                                        sessionIndex + 1
                                                      ]?.[outcomeIndex];
                                                    const prevReps =
                                                      findPreviousValue(
                                                        sortedPeriods,
                                                        periodIndex,
                                                        (p) =>
                                                          at(p)?.outcomeReps ??
                                                          null
                                                      );
                                                    const prevRpe =
                                                      findPreviousValue(
                                                        sortedPeriods,
                                                        periodIndex,
                                                        (p) =>
                                                          at(p)?.outcomeRpe ??
                                                          null
                                                      );
                                                    const prevLoad =
                                                      findPreviousValue(
                                                        sortedPeriods,
                                                        periodIndex,
                                                        (p) =>
                                                          at(p)?.outcomeLoad ??
                                                          null
                                                      );
                                                    if (
                                                      prevReps == null &&
                                                      prevRpe == null &&
                                                      prevLoad == null
                                                    )
                                                      return null;

                                                    const dispLoad =
                                                      outcomeLoad != null
                                                        ? _.round(
                                                            fromMetric(
                                                              outcomeLoad,
                                                              units
                                                            ),
                                                            precision
                                                          )
                                                        : null;
                                                    const prevDispLoad =
                                                      prevLoad != null
                                                        ? _.round(
                                                            fromMetric(
                                                              prevLoad,
                                                              units
                                                            ),
                                                            precision
                                                          )
                                                        : null;

                                                    return (
                                                      <span className="trend-icons">
                                                        <TrendIndicator
                                                          curr={outcomeReps}
                                                          prev={prevReps}
                                                          metric="reps"
                                                          letter="R"
                                                          label={t(
                                                            "blockReview.reps"
                                                          )}
                                                        />
                                                        <TrendIndicator
                                                          curr={outcomeRpe}
                                                          prev={prevRpe}
                                                          metric="rpe"
                                                          letter="E"
                                                          label={intensityMeasure}
                                                        />
                                                        <TrendIndicator
                                                          curr={dispLoad}
                                                          prev={prevDispLoad}
                                                          metric="load"
                                                          letter="L"
                                                          label={t(
                                                            "blockReview.load"
                                                          )}
                                                          unit={unitsLabel}
                                                        />
                                                      </span>
                                                    );
                                                  })()}
                                              </div>
                                            </div>
                                          )
                                        );
                                      })}
                                    </div>
                                  )
                                )}

                              {/* Metrics */}
                              <p className="blank-target">&nbsp;</p>
                              <div className="session-titles">
                                <p className="s1">
                                  {!!exerciseData.periods[periodKey].hasData &&
                                    `${
                                      _.round(
                                        fromMetric(
                                          exerciseData.periods[periodKey]
                                            .maxWeight,
                                          units
                                        ),
                                        precision
                                      ) || " - "
                                    }${unitsLabel}`}
                                  {showTrends &&
                                    exerciseData.periods[periodKey].hasData && (
                                      <TrendIndicator
                                        curr={
                                          _.round(
                                            fromMetric(
                                              exerciseData.periods[periodKey]
                                                .maxWeight,
                                              units
                                            ),
                                            precision
                                          ) || null
                                        }
                                        prev={
                                          prevMaxWeight != null
                                            ? _.round(
                                                fromMetric(
                                                  prevMaxWeight,
                                                  units
                                                ),
                                                precision
                                              )
                                            : null
                                        }
                                        metric="maxWeight"
                                        label={t("blockReview.maxWeight")}
                                        unit={unitsLabel}
                                      />
                                    )}
                                </p>
                                <p className="s1">
                                  {!!exerciseData.periods[periodKey].hasData &&
                                    `${
                                      _.round(
                                        fromMetric(
                                          exerciseData.periods[periodKey]
                                            .topE1rm,
                                          units
                                        ),
                                        precision
                                      ) || " - "
                                    }${unitsLabel}`}
                                  {showTrends &&
                                    exerciseData.periods[periodKey].hasData && (
                                      <TrendIndicator
                                        curr={
                                          _.round(
                                            fromMetric(
                                              exerciseData.periods[periodKey]
                                                .topE1rm,
                                              units
                                            ),
                                            precision
                                          ) || null
                                        }
                                        prev={
                                          prevTopE1rm != null
                                            ? _.round(
                                                fromMetric(
                                                  prevTopE1rm,
                                                  units
                                                ),
                                                precision
                                              )
                                            : null
                                        }
                                        metric="e1rm"
                                        label={t("blockReview.topE1rm")}
                                        unit={unitsLabel}
                                      />
                                    )}
                                </p>
                                <p className="s1">
                                  {!!exerciseData.periods[periodKey].hasData &&
                                    `${
                                      _.round(
                                        fromMetric(
                                          exerciseData.periods[periodKey]
                                            .volume,
                                          units
                                        ),
                                        precision
                                      ) || " - "
                                    }${unitsLabel}`}
                                  {showTrends &&
                                    exerciseData.periods[periodKey].hasData && (
                                      <TrendIndicator
                                        curr={
                                          _.round(
                                            fromMetric(
                                              exerciseData.periods[periodKey]
                                                .volume,
                                              units
                                            ),
                                            precision
                                          ) || null
                                        }
                                        prev={
                                          prevVolume != null
                                            ? _.round(
                                                fromMetric(
                                                  prevVolume,
                                                  units
                                                ),
                                                precision
                                              )
                                            : null
                                        }
                                        metric="volume"
                                        label={t("blockReview.volume")}
                                        unit={unitsLabel}
                                      />
                                    )}
                                </p>
                              </div>
                            </div>
                          </td>
                          );
                        })}
                    </tr>
                  );
                })}

                {!!metricsDataKeys.length && (
                  <>
                    {/* Volume */}
                    <tr className="block-review-metrics-row">
                      <td colSpan={2}>
                        <p>{t("blockReview.volume")}</p>
                      </td>

                      {metricsDataKeys.map((metricKey) => (
                        <td key={`metric-volume-${metricKey}`} colSpan={2}>
                          <p>
                            {_.round(
                              fromMetric(
                                metricsBlockData[metricKey].volume,
                                units
                              ),
                              precision
                            ) || " - "}
                            {unitsLabel}
                          </p>
                        </td>
                      ))}
                    </tr>
                    {/* Sets by muscle group */}
                    <tr className="block-review-metrics-row">
                      <td colSpan={2}>
                        <p>{t("blockReview.setsByMuscleGroup")}</p>
                      </td>

                      {metricsDataKeys.map((metricKey) => (
                        <td key={`metric-sets-${metricKey}`} colSpan={2}>
                          {MUSCLE_GROUP_ARRAY.map((muscle) => {
                            if (muscle !== "None") {
                              return (
                                <div
                                  key={`metric-sets-${metricKey}-${muscle}`}
                                  style={{
                                    display: "flex",
                                    flexDirection: "row",
                                    justifyContent: "space-between",
                                  }}
                                >
                                  <span>
                                    {t(
                                      `common:muscleGroup.${muscleGroupToI18nKey(
                                        muscle
                                      )}`
                                    )}
                                  </span>
                                  <span>
                                    {metricsBlockData[metricKey].sets[muscle] ||
                                      "-"}
                                  </span>
                                </div>
                              );
                            }
                          })}
                          <div
                            key={`metric-sets-${metricKey}-xdxdxd`}
                            style={{
                              display: "flex",
                              flexDirection: "row",
                              justifyContent: "space-between",
                            }}
                          >
                            <span className="font-bold">
                              &nbsp;&nbsp;{t("blockReview.total")}
                            </span>
                            <span className="font-bold">
                              {Object.values(
                                metricsBlockData[metricKey].sets
                              ).reduce((sum, value) => sum + value, 0) || "-"}
                            </span>
                          </div>
                        </td>
                      ))}
                    </tr>
                    {/* Reps by muscle group */}
                    <tr className="block-review-metrics-row">
                      <td colSpan={2}>
                        <p>{t("blockReview.repsByMuscleGroup")}</p>
                      </td>

                      {metricsDataKeys.map((metricKey) => (
                        <td key={`metric-reps-${metricKey}`} colSpan={2}>
                          {MUSCLE_GROUP_ARRAY.map((muscle) => {
                            if (muscle !== "None") {
                              return (
                                <div
                                  key={`metric-reps-${metricKey}-${muscle}`}
                                  style={{
                                    display: "flex",
                                    flexDirection: "row",
                                    justifyContent: "space-between",
                                  }}
                                >
                                  <span>
                                    {t(
                                      `common:muscleGroup.${muscleGroupToI18nKey(
                                        muscle
                                      )}`
                                    )}
                                  </span>
                                  <span>
                                    {metricsBlockData[metricKey].reps[muscle] ||
                                      "-"}
                                  </span>
                                </div>
                              );
                            }
                          })}
                          <div
                            key={`metric-sets-${metricKey}-xdxdxd`}
                            style={{
                              display: "flex",
                              flexDirection: "row",
                              justifyContent: "space-between",
                            }}
                          >
                            <span className="font-bold">
                              &nbsp;&nbsp;{t("blockReview.total")}
                            </span>
                            <span className="font-bold">
                              {Object.values(
                                metricsBlockData[metricKey].reps
                              ).reduce((sum, value) => sum + value, 0) || "-"}
                            </span>
                          </div>
                        </td>
                      ))}
                    </tr>
                  </>
                )}
              </tbody>
            </table>
          ) : (
            <div>
              <p className="p1">{t("blockReview.noExerciseData")}</p>
            </div>
          )}
          </section>

          {insightsActive && programInsightsInput && (
            <div className="Program-Editor Program-Editor--insights-host-only">
              <ProgramInsightsShell
                cycleId={insightsStorageId}
                builderScrollRef={tableScrollRef}
                builderWeeks={programInsightsInput.builderWeeks}
                programExercises={programInsightsInput.programExercises}
                weekHeaderLabels={programInsightsInput.weekHeaderLabels}
                fromMetric={fromMetric}
                insightsDataSource={INSIGHTS_DATA_SOURCE.OUTCOMES}
                coachPreferences={{
                  ...activeUserToRequest,
                  defaultInsightsLens: getEffectiveDefaultInsightsLens(
                    activeUser,
                    organizationOwner,
                    clientConfiguration,
                  ),
                }}
              />
            </div>
          )}
        </div>
      </article>
    </main>
  );
};

const STATUS_CLASS_NAME = {
  completed: "circle-status completed",
  missed: "circle-status missed",
  incomplete: "circle-status incomplete",
};

BlockReview.propTypes = {
  locals: PropTypes.shape({
    query: PropTypes.shape({
      from: PropTypes.string,
      to: PropTypes.string,
      exerciseFilters: PropTypes.string,
      activeRange: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    }).isRequired,
    pathname: PropTypes.string,
    weeksExercises: PropTypes.arrayOf(PropTypes.object),
    activeUser: PropTypes.object.isRequired,
    activeUserToRequest: PropTypes.object,
    withWeight: PropTypes.func,
    fromMetric: PropTypes.func,
    organizationOwner: PropTypes.object,
    clientConfiguration: PropTypes.object,
    athlete: PropTypes.object,
  }).isRequired,
};

export default BlockReview;
